import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApp } from "@/components/AppShell";
import { useRealtime, realtimeStatus } from "@/lib/realtime";
import { typeMeta, prioMeta, STATUSES, initials } from "@/lib/meta";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Circle, CheckCircle2, Bug, FolderKanban, ListTodo, Zap, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const { openIssue, projects, users } = useApp();
  const [stats, setStats] = useState(null);
  const [myIssues, setMyIssues] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => { (async () => {
    const [s, mine, r] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/issues", { params: { assignee_id: user.id, limit: 20 } }),
      api.get("/issues", { params: { limit: 8 } }),
    ]);
    setStats(s.data); setMyIssues(mine.data); setRecent(r.data);
  })(); }, [user.id]);

  useRealtime(async (ev) => {
    if (["issue.created","issue.updated","issue.deleted"].includes(ev.type)) {
      const [s, mine, r] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/issues", { params: { assignee_id: user.id, limit: 20 } }),
        api.get("/issues", { params: { limit: 8 } }),
      ]);
      setStats(s.data); setMyIssues(mine.data); setRecent(r.data);
    }
  }, [user.id]);

  const prioData = stats ? Object.entries(stats.priorities).map(([k,v]) => ({name:k,value:v})) : [];
  const COLORS = ["#ef4444","#f97316","#f59e0b","#0ea5e9","#64748b"];
  const statusChart = STATUSES.map(s => ({ name: s.label, count: recent.filter(i => i.status === s.id).length }));

  return (
    <div className="px-6 py-6 max-w-[1600px]">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Welcome back</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">{user?.name?.split(" ")[0] || "there"} — here's what's on your plate.</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={ListTodo} label="Open issues" value={stats?.open ?? "—"} />
        <Stat icon={CheckCircle2} label="Done" value={stats?.done ?? "—"} accent="text-emerald-500" />
        <Stat icon={Bug} label="Open bugs" value={stats?.bugs_open ?? "—"} accent="text-red-500" />
        <Stat icon={Zap} label="Active sprints" value={stats?.active_sprints ?? "—"} accent="text-primary" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">Assigned to you</h2>
            <span className="text-xs text-muted-foreground">{myIssues.length} issues</span>
          </div>
          {myIssues.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center">Nothing assigned. Enjoy the calm.</div>
          ) : (
            <div className="divide-y divide-border -mx-2">
              {myIssues.slice(0,10).map(i => {
                const T = typeMeta(i.issue_type); const P = prioMeta(i.priority);
                return (
                  <button key={i.id} onClick={()=>openIssue(i.id)} data-testid={`dash-my-${i.key}`} className="w-full px-2 py-2.5 flex items-center gap-3 hover:bg-accent rounded-md text-left transition-colors">
                    <T.icon className={`w-4 h-4 ${T.cls} shrink-0`} />
                    <span className="font-mono text-xs text-muted-foreground w-16 shrink-0">{i.key}</span>
                    <span className="text-sm flex-1 truncate">{i.summary}</span>
                    <P.icon className={`w-4 h-4 ${P.cls}`} />
                    <span className={`text-[11px] font-mono px-2 py-0.5 rounded border status-${i.status}`}>{STATUSES.find(s=>s.id===i.status)?.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
        <Card className="p-5">
          <h2 className="font-display text-lg font-semibold mb-4">Priority breakdown</h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={prioData} innerRadius={40} outerRadius={70} dataKey="value" nameKey="name" paddingAngle={2}>
                  {prioData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:6,fontSize:12}} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {prioData.map((p,i)=>(
              <div key={p.name} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{background:COLORS[i]}} />
                <span className="capitalize text-muted-foreground">{p.name}</span>
                <span className="ml-auto font-mono">{p.value}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card className="lg:col-span-2 p-5">
          <h2 className="font-display text-lg font-semibold mb-4">Recent status distribution</h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                <Tooltip contentStyle={{background:"hsl(var(--popover))",border:"1px solid hsl(var(--border))",borderRadius:6,fontSize:12}} />
                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5">
          <h2 className="font-display text-lg font-semibold mb-4">Projects</h2>
          <div className="space-y-2">
            {projects.slice(0,6).map(p => (
              <Link key={p.id} to={`/projects/${p.id}`} data-testid={`dash-proj-${p.key}`} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent transition-colors">
                <div className="w-8 h-8 grid place-items-center rounded bg-primary/15 text-primary font-mono text-xs font-semibold">{p.key.slice(0,2)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.description || "No description"}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className={`w-9 h-9 grid place-items-center rounded-md bg-secondary ${accent || "text-primary"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
      </div>
    </Card>
  );
}
